import React, { useState, useEffect, useRef } from 'react';
import Storage from '@aws-amplify/storage';
import { v4 as uuid } from 'uuid';
import { useDropArea } from 'react-use';
import Input from 'components/Input';
import Button from 'components/Button';
import awsconfig from 'aws-exports';
import { useHasura } from 'context/ApiState';

const MB = 1000000;
const MAX_FILE_SIZE = 1 * MB;

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket,
} = awsconfig; // destructure the aws config to get our region and bucket name

const AppUserImageUploader = ({ className = '' }) => {
  const { app_user, updateAppUser, addFileUpload } = useHasura();

  const fileBrowserEl = useRef<HTMLInputElement>();
  const [file, setFile] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [imgDimensions, setImgDimensions] = useState(null);
  const [bond, state] = useDropArea({
    onFiles: (files) => !uploadInProgress && setFile(files[0]),
  });

  const clickOnFileBrowserEl = (event) => {
    event.preventDefault();
    if (fileBrowserEl?.current) fileBrowserEl?.current?.click();
  };

  useEffect(() => {
    if (!uploadInProgress && file?.name) {
      if (file?.size > MAX_FILE_SIZE) {
        alert(`Please pick a file smaller than ${MAX_FILE_SIZE / MB} MB.`);
        return;
      }

      if (!/^image\/(png|jpe?g|gif|svg\+xml)$/.test(file?.type)) {
        alert('Unsupported file type, please upload a PNG/JPEG/GIF');
        return;
      }

      const img = new Image();
      img.addEventListener('load', (event: any) => {
        setImgDimensions({
          width: event?.target?.width ?? 0,
          height: event?.target?.height ?? 0,
        });
        window.URL.revokeObjectURL(img.src); // free some memory
      });
      img.src = window.URL.createObjectURL(file);
    }
  }, [file]);

  const filePickerEventHandler = (event) => {
    event.preventDefault();
    setFile(event.target.files[0]);
  };

  const uploadFileAndReplaceAppUserImage = async () => {
    if (file) {
      const extension = file?.name?.split('.').pop() || '';
      const { type: mimeType } = file;
      const id = uuid();
      const s3_key = `global/${id}.${extension}`;
      const public_url = `https://${bucket}.s3.${region}.amazonaws.com/public/${s3_key}`;

      try {
        setUploadInProgress(true);

        console.log(`Uploading file [${s3_key}]...`);
        const storagePutResult = await Storage.put(s3_key, file, {
          contentType: mimeType,
          // level: "protected"
        });

        console.log(`Adding Upload record [${id}]...`);
        const addFileUploadResult = await addFileUpload({
          id,
          s3_key,
          public_url,
          s3_level: 'public',
          description: `profile image`,
        });

        console.log(`Updating AppUser record [${app_user?.id}]...`);
        const updateAppUserResult = await updateAppUser({
          image_upload_id: id,
        });

        console.log(storagePutResult, addFileUploadResult, updateAppUserResult);

        setFile(null);
      } catch (err) {
        console.log('error: ', err);
      } finally {
        setUploadInProgress(false);
      }
    }
  };

  return (
    <div
      className={`rounded flex h-48 items-start box-border border-dotted text-gray-600 ${className}`}
      {...bond}
    >
      <div className="flex flex-col items-end justify-between flex-grow h-full">
        <div
          className={`flex-grow flex items-center justify-center w-full border-2 p-4 rounded-lg border-dotted select-none cursor-default text-sm ${
            state.over
              ? 'border-jcap-500 bg-jcap-200 text-jcap-500'
              : 'border-gray-400 text-gray-500'
          }`}
        >
          {uploadInProgress ? (
            <div className="text-xs text-gray-600 pointer-events-none">
              Please wait...
            </div>
          ) : file?.size ? (
            <div className="text-xs text-gray-600 pointer-events-none">
              <p>{file?.name}</p>
              {imgDimensions && (
                <p>{`${imgDimensions?.width} x ${imgDimensions?.height}`}</p>
              )}
              <p>{(file?.size / MB).toFixed(2)} MB</p>
            </div>
          ) : (
            `Drag image file here, or click button to browse`
          )}
        </div>

        <div className="flex-shrink-0 pb-1 -m-1">
          <Input
            type="file"
            accept=".jpg,.jpeg,.gif,.png,.svg"
            onChange={filePickerEventHandler}
            ref={fileBrowserEl}
            hidden
          />
          {file ? (
            <Button
              className="h-10 mr-1 text-sm btn btn-white"
              onClick={() => setFile(null)}
              disabled={uploadInProgress}
            >
              Clear
            </Button>
          ) : (
            <Button
              className="h-10 mr-1 text-sm btn btn-white"
              onClick={clickOnFileBrowserEl}
              disabled={uploadInProgress}
            >
              Browse
            </Button>
          )}
          <Button
            className="h-10 text-sm btn"
            onClick={uploadFileAndReplaceAppUserImage}
            disabled={!file || uploadInProgress}
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AppUserImageUploader;
